// Tarjeta del bloque `propuesta_diagnostico` en el ChatPane del SO (ADR-007 §6).
// El agente PROPONE y la persona CONFIRMA: «Crear diagnóstico» hace
// POST /api/v1/diagnostics/projects en borrador. Nada se crea sin el clic.
// Respeta la empresa elegida (X-Acting-Org vía el cliente API del módulo) y lo
// dice en la tarjeta: «Se crea en: <empresa>». Diseño del panel de public-sites
// (ProposalCard) adaptado al lenguaje del SO: tokens semánticos, tarjeta 20 px.
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { BadgeCheck, Building2, ExternalLink, Lightbulb, Loader2, Plus, Scale, Users } from 'lucide-react';

import { useActingOrg } from '@/lib/actingOrg';
import * as api from '../api';
import type { DiagProject } from '../types';
import {
  citaLabel, objetivoConTemas, parsePropuesta, resumenDe, servicioDe, serviciosDisponibles, tipoDef, normTipo,
  type Cita, type DiagService, type Propuesta,
} from '../propuesta';

function CitaChips({ citas }: { citas: Cita[] }) {
  if (!citas.length) return null;
  const cls = 'inline-flex max-w-full items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[11.5px] font-medium text-primary';
  return (
    <ul className="mt-1 flex flex-wrap gap-1.5" aria-label="Fuentes">
      {citas.map((c, i) => (
        <li key={i} className="min-w-0 max-w-full">
          {c.fuente_url ? (
            <a href={c.fuente_url} target="_blank" rel="noreferrer noopener" className={`${cls} hover:bg-primary/15`} title={c.titulo || undefined}>
              <BadgeCheck size={13} className="shrink-0" aria-hidden />
              <span className="truncate">{citaLabel(c)}</span>
              <ExternalLink size={11} className="shrink-0 opacity-70" aria-hidden />
              <span className="sr-only">(fuente oficial, se abre en otra pestaña)</span>
            </a>
          ) : (
            <span className={cls} title={c.titulo || undefined}>
              <BadgeCheck size={13} className="shrink-0" aria-hidden />
              <span className="truncate">{citaLabel(c)}</span>
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

function SectionTitle({ icon, children }: { icon?: ReactNode; children: ReactNode }) {
  return (
    <p className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
      {icon}
      {children}
    </p>
  );
}

/** Renderiza el bloque; si no valida, cae al texto del resumen (`renderFallback`). */
export function PropuestaDiagnosticoBloque({ bloque, renderFallback }: { bloque: unknown; renderFallback: (t: string) => ReactNode }) {
  const p = useMemo(() => parsePropuesta(bloque), [bloque]);
  const resumen = resumenDe(bloque);
  if (!p) return <>{resumen ? renderFallback(resumen) : null}</>;
  return <PropuestaDiagnosticoCard propuesta={p} resumen={resumen} />;
}

export function PropuestaDiagnosticoCard({ propuesta: p, resumen }: { propuesta: Propuesta; resumen: string }) {
  const { acting } = useActingOrg();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState<DiagProject | null>(null);
  const [servicio, setServicio] = useState<DiagService | null>(null);
  const def = tipoDef(p.tipo);
  const tipo = normTipo(p.tipo);
  // Dentro de una empresa del SO, el proyecto es de ESA empresa (no de la que diga el modelo).
  const cliente = acting ? acting.nombre : p.cliente;
  const sinPermiso = !!acting && !acting.scopes.includes('diagnostics:create');

  useEffect(() => {
    let vivo = true;
    api
      .catalogo()
      .then((raw) => vivo && setServicio(servicioDe(p, serviciosDisponibles(raw))))
      .catch(() => vivo && setServicio(null));
    return () => {
      vivo = false;
    };
    // La empresa elegida cambia el catálogo visible (X-Acting-Org). Solo depende del tipo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.tipo, acting?.org_id]);

  const crear = async () => {
    if (busy || created) return;
    if (!cliente.trim()) {
      setError('Falta el nombre de la empresa cliente: pídeselo al asistente o elige la empresa arriba, en Diagnósticos.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const pr = await api.createProject({
        client_name: cliente.trim(),
        name: (p.nombre || `Diagnóstico de ${(servicio?.nombre || def.label).toLowerCase()}`).trim(),
        objective: objetivoConTemas(p),
        ...(servicio ? { servicio: servicio.slug, tipo: servicio.tipo || tipo } : { tipo }),
        enfoque: def.pideEnfoque ? p.enfoque : '',
        status: 'borrador',
      });
      setCreated(pr);
    } catch (e) {
      // Con empresa elegida, un 403 trae el motivo del servidor (y ya volvió a mi org).
      setError((e as Error).message || 'No pudimos crear el diagnóstico.');
    } finally {
      setBusy(false);
    }
  };

  const titulo = p.nombre || def.label;
  return (
    <article aria-label={`Propuesta: ${titulo}`} className="orion-card w-full min-w-0 rounded-[20px] border border-border bg-card p-4 text-card-foreground sm:p-5">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-primary">Propuesta de diagnóstico</p>
      <h4 className="mt-1 text-base font-semibold leading-snug text-foreground">{titulo}</h4>
      <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs">
        <span className="rounded-full bg-primary/10 px-2 py-0.5 font-medium text-primary">{servicio?.nombre || def.label}</span>
        {cliente && <span className="rounded-full border border-border px-2 py-0.5 text-foreground">{cliente}</span>}
        {p.enfoque && def.pideEnfoque && <span className="rounded-full border border-border px-2 py-0.5 text-foreground">{p.enfoque}</span>}
      </div>
      {resumen && <p className="mt-3 text-sm text-muted-foreground">{resumen}</p>}
      {p.objetivo && (
        <div className="mt-3">
          <SectionTitle>Objetivo</SectionTitle>
          <p className="text-sm text-foreground">{p.objetivo}</p>
        </div>
      )}
      {p.temas_obligatorios.length > 0 && (
        <div className="mt-3">
          <SectionTitle icon={<Scale size={13} aria-hidden />}>Temas obligatorios</SectionTitle>
          <ul className="space-y-2">
            {p.temas_obligatorios.map((t, i) => (
              <li key={i} className="text-sm text-foreground">
                <span className="font-medium">{t.tema}</span>
                {t.norma || t.fuente_url ? (
                  <CitaChips citas={[t]} />
                ) : (
                  <span className="ml-1 text-xs text-muted-foreground">· sin cita en la base verificada</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      {p.temas_sugeridos.length > 0 && (
        <div className="mt-3">
          <SectionTitle icon={<Lightbulb size={13} aria-hidden />}>Temas sugeridos</SectionTitle>
          <ul className="space-y-1.5">
            {p.temas_sugeridos.map((t, i) => (
              <li key={i} className="text-sm text-foreground">
                <span className="font-medium">{t.tema}</span>
                {t.fundamento && <span className="text-muted-foreground"> — {t.fundamento}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
      {p.participantes_sugeridos.length > 0 && (
        <div className="mt-3">
          <SectionTitle icon={<Users size={13} aria-hidden />}>A quién entrevistar</SectionTitle>
          <p className="text-sm text-muted-foreground">{p.participantes_sugeridos.join(' · ')}</p>
        </div>
      )}

      <div className="mt-4 border-t border-border pt-3">
        <p className="mb-2 flex items-center gap-1.5 text-xs text-muted-foreground">
          <Building2 size={13} aria-hidden className="text-primary" />
          Se crea en: <span className="font-medium text-foreground">{acting ? acting.nombre : 'tu organización'}</span>
          {acting && <span>· como {acting.consultora_nombre || 'tu consultora'}</span>}
        </p>
        {created ? (
          <p role="status" className="flex flex-wrap items-center gap-2 text-sm text-foreground">
            <BadgeCheck size={16} aria-hidden className="text-emerald-600 dark:text-emerald-400" />
            <span>Listo: se creó «{created.name}» en borrador.</span>
            <a href={`/m/diagnosticos/proyectos?p=${encodeURIComponent(created.id)}`} className="font-medium text-primary underline-offset-2 hover:underline">
              Abrir y agregar participantes
            </a>
          </p>
        ) : (
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <button
              type="button"
              onClick={crear}
              disabled={busy || sinPermiso}
              className="orion-btn-brand inline-flex min-h-[40px] items-center justify-center gap-1.5 rounded-xl bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground transition disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? <Loader2 size={15} className="animate-spin" aria-hidden /> : <Plus size={15} aria-hidden />} Crear diagnóstico
            </button>
            <span className="text-xs text-muted-foreground">
              {sinPermiso ? `Tu acceso a ${acting?.nombre} es de solo lectura.` : 'Se crea en borrador; puedes editarlo antes de invitar.'}
            </span>
          </div>
        )}
        {error && <p role="alert" className="mt-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}
      </div>
    </article>
  );
}
